import { Dispatch, SetStateAction } from 'react';
import { SkuParseDto } from 'marketplace/shared/graphCms/skuParse.dto';
import { generateSku } from '../../global/ProductHelpers';
import { lowerFirst } from '../../../../../utils/Utils';

import { ProductStyleCMS, ProductStyleConfigurationCMS } from './types';
import { OnSelectFunction, ProductDto } from '../../../types/schema';
import { StyleSelectorContainerProps } from './StyleSelectorContainer';
import { SkuPart } from '../../../../../shared/skuSplit';

export const findInitial = (skuSplit: ProductDto['skuSplit']) => ({
  skuPartOnSelectPairs,
}: Pick<ProductStyleConfigurationCMS, 'skuPartOnSelectPairs'>) => {
  if (!skuPartOnSelectPairs || skuPartOnSelectPairs.length === 0) {
    return false;
  }

  return skuPartOnSelectPairs.every(({
    onSelectFunction,
    skuPartValue,
  }) => (
    skuPartValue === skuSplit[lowerFirst(onSelectFunction.slice(3) as SkuPart)]
  ));
};

export const initialStyleAndConfig = (
  skuSplit: ProductDto['skuSplit'] | null,
  productStyles?: ProductStyleCMS[],
) => {
  let config: ProductStyleConfigurationCMS | null = null;
  let style: ProductStyleCMS | null = null;

  if (skuSplit) {
    let configIndex = -1;
    style = productStyles?.find((s) => {
      configIndex = s.configurations.findIndex(findInitial(skuSplit));
      return configIndex >= 0;
    }) ?? null;

    config = style?.configurations[configIndex] ?? null;
  }

  return {
    style,
    config,
  };
};

export const generateSkuArg = ({
  skuPartOnSelectPairs,
}: Pick<ProductStyleConfigurationCMS, 'skuPartOnSelectPairs'>) => {
  if (skuPartOnSelectPairs?.length) {
    return skuPartOnSelectPairs.reduce((obj, { onSelectFunction, skuPartValue }) => {
      obj[lowerFirst(onSelectFunction)] = skuPartValue;
      return obj;
    }, {} as Partial<Record<Uncapitalize<OnSelectFunction>, string>>);
  }

  return {};
};

export const generateSkuToSetup = (
  skuSplit: ProductDto['skuSplit'],
  config: ProductStyleConfigurationCMS | null,
  style: ProductStyleCMS | null,
  skuParse: SkuParseDto,
) => {
  if (!config || !style) return null;

  const sku = generateSku(
    generateSkuArg(config),
    skuSplit,
    skuParse,
  );

  return {
    sku,
    fallbackSku: config.fallbackSku,
  };
};

export const onSelectStyleHandler = (
  productStyles: ProductStyleCMS[],
  setActiveStyle: Dispatch<SetStateAction<ProductStyleCMS | null>>,
  onSelectCb?: StyleSelectorContainerProps['onSelectCb'],
) => (styleId: ProductStyleCMS['id'] | null) => {
  const selectedStyle = productStyles?.find(({ id }) => id === styleId) || null;
  setActiveStyle(selectedStyle);

  if (typeof onSelectCb === 'function' && selectedStyle?.fallbackSku) {
    onSelectCb({ sku: selectedStyle.fallbackSku });
  }
};

export const onSelectConfigHandler = (
  activeStyle: ProductStyleCMS | null,
  setActiveConfiguration: Dispatch<SetStateAction<ProductStyleConfigurationCMS | null>>,
  skuSplit: ProductDto['skuSplit'],
  onSelectCb?: StyleSelectorContainerProps['onSelectCb'],
  skuParse?: SkuParseDto,
) => (configuration: ProductStyleConfigurationCMS) => {
  setActiveConfiguration(configuration);

  if (typeof onSelectCb === 'function') {
    const skuToSetup = generateSkuToSetup(skuSplit, configuration, activeStyle, skuParse);
    if (skuToSetup) {
      onSelectCb(skuToSetup);
    }
  }
};
